import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  getPendingOvertimeApprovals, getOvertimeApprovals,
  decideOvertimeApproval, decideOvertimeApprovalsBulk,
} from '../../../services/overtimeApprovalService';
import formatMinutes from '../../../utils/formatMinutes';
import timesheetStatusColors from '../../../utils/timesheetStatusColors';
import { translateTimesheetStatuses } from '../../../utils/timesheetStatusLabels';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';
import Tooltip from '../../../components/ui/Tooltip';
import { useTaFilterColumns } from '../../pdks-reports/config/taFilterColumns';

// Pending (DWS.Status — normal/overtime/vb.) rozeti; Eksik Kayıtlar'daki (config/index.js)
// StatusBadge ile AYNI görsel — sayfa-lokal kopya (repoda paylaşımlı bir StatusBadge bileşeni yok;
// PendingApprovalsPage/LeaveDetailPage'in ret dialog'u da AYNI şekilde sayfa-lokal kopyalanmıştır).
const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg || '#9e9e9e15'};
  color: ${({ $color }) => $color || '#9e9e9e'};
`;

// Kararlar (approved/rejected) rozeti — renkler usePendingLeaveApi'nin StatusBadge'iyle BİREBİR
// (brief: "renkler leave onay sayfasındaki yeşil/kırmızı desen").
const DecisionBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) => ($status === 'approved' ? '#2e7d3215' : '#d32f2f15')};
  color: ${({ $status }) => ($status === 'approved' ? '#2e7d32' : '#d32f2f')};
`;

const StaleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: fit-content;
`;

const StaleBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: #fef9c3;
  color: #ca8a04;
`;

const StaleDetail = styled.span`
  font-size: 11px;
  color: #6b7280;
  font-family: 'Nunito Sans', sans-serif;
`;

// Eski (AKM POC öncesi) karar: ölçü "vardiya dışı dakika"ydı, hesaplanan fazla mesai değil.
const LegacyNote = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  font-family: 'Nunito Sans', sans-serif;
`;

// Hesaplanan fazla mesai kovaları: etiketleri "(Dk)" taşır, değer de DAKİKA olarak basılır (karar
// çekmecesindeki onaylanan dakika alanıyla aynı birim).
const minutesColumn = (name, label) => ({
  name, label,
  options: { filter: false, sort: false, customBodyRender: (v) => (Number.isFinite(v) ? v : 0) },
});

// Kararın ölçüsü: yeni kayıtta onaylanabilir HESAPLANAN fazla mesai (measuredOvertimeMinutes), eski
// kayıtta (null) vardiya dışı dakika (measuredOutsideMinutes). Bayatlık karşılaştırması da aynı ölçüyle.
export const isLegacyDecision = (row) => row?.measuredOvertimeMinutes === null || row?.measuredOvertimeMinutes === undefined;
export const decisionMeasured = (row) => (isLegacyDecision(row) ? row?.measuredOutsideMinutes : row?.measuredOvertimeMinutes);
export const decisionCurrent = (row) => (isLegacyDecision(row) ? row?.currentOutsideMinutes : row?.currentApprovableMinutes);

// FirstEntry/LastExit DateTime? (ISO string) — duvar-saati göstermek için 'T' sonrası HH:mm DOĞRUDAN
// alınır (Eksik Kayıtlar'ın formatClock'uyla BİREBİR aynı — new Date().getHours() tarayıcı saat
// dilimine göre kaydırırdı).
const formatClock = (val) => {
  if (!val) return '-';
  const s = String(val);
  const tIdx = s.indexOf('T');
  if (tIdx >= 0 && s.length >= tIdx + 6) return s.substring(tIdx + 1, tIdx + 6);
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '-';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const emptyPagination = { totalRowCount: 0, pageSize: 10, pageIndex: 0 };

// GET /pending — henüz karar verilmemiş HESAPLANAN fazla mesaili günler (AKM POC §5.1) + satır aksiyonları
// (approve/reject tekil karar burada verilir, toplu onay decideBulk ile). Onaylanabilir = hesaplanan fazla
// mesai − önceden onaylı (planlı fazla mesai vardiyası); vardiya dışı dakika yalnız bilgi sütunudur. usePendingLeaveApi (leaves/config) ile AYNI şekilde
// fetch+aksiyon TEK hook'ta toplanmıştır (en yakın davranışsal eşleşme: liste + onay/ret aksiyonu).
export const usePendingOvertimeApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getPendingOvertimeApprovals(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => setData([]))
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  // Tekil karar (Onayla/Reddet). Payload: {userId,date,status,approvedMinutes,notes}. Başarıda kendi
  // listesini yeniler; hata interceptor'ca toast'lanır (mutasyon → reject; burada yutulur, çağıran
  // sayfa dialog'u açık tutar).
  const decide = useCallback(async (payload) => {
    try {
      await decideOvertimeApproval(payload);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Toplu onay — items: [{userId,date,status:'approved',approvedMinutes}]. Backend HTTP 200 ile
  // {succeeded,failed[]} döner (kısmi başarı da 200'dür); sadece ağ/istisna hatasında null döner.
  const decideBulk = useCallback(async (items) => {
    try {
      const res = await decideOvertimeApprovalsBulk({ items });
      setRefreshTrigger((p) => p + 1);
      return res.data?.data || { succeeded: 0, failed: [] };
    } catch {
      return null;
    }
  }, []);

  // Tek durum haritası (utils/timesheetStatusLabels) — partialLeave dahil hiçbir durum ham görünmez.
  const statusLabels = useMemo(() => translateTimesheetStatuses(t), [t]);
  // AKM POC §7.5 — ortak PDKS süzgeç seti (tabloda görünmez); ekip kapsamı sunucuda.
  const filterColumns = useTaFilterColumns(t);

  const columns = useMemo(() => [
    { name: 'summaryId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('timesheet.employee'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={v} profilePicture={row?.userProfilePicture} />;
        },
      },
    },
    dateFilterColumn('date', t('timesheet.date'), lang),
    minutesColumn('overtimeMinutes', t('overtimeApproval.overtimeMinutes')),
    minutesColumn('excessHoursMinutes', t('overtimeApproval.excessHoursMinutes')),
    minutesColumn('weeklyRestWorkMinutes', t('overtimeApproval.weeklyRestWorkMinutes')),
    minutesColumn('holidayWorkMinutes', t('overtimeApproval.holidayWorkMinutes')),
    minutesColumn('preApprovedMinutes', t('overtimeApproval.preApprovedMinutes')),
    minutesColumn('approvableMinutes', t('overtimeApproval.approvableMinutes')),
    {
      // Bilgi sütunu: vardiya dışı süre (erken geliş + geç çıkış) — onayın ölçüsü DEĞİL.
      name: 'outsideMinutes', label: t('timesheet.outside'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'nightOutsideMinutes', label: t('overtimeApproval.nightOutside'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'firstEntry', label: t('pdks.firstEntry'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatClock(v) },
    },
    {
      name: 'lastExit', label: t('pdks.lastExit'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatClock(v) },
    },
    {
      name: 'status', label: t('common.status'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => {
          const colors = timesheetStatusColors[v] || { bg: '#9e9e9e15', text: '#9e9e9e' };
          return <StatusBadge $bg={colors.bg} $color={colors.text}>{statusLabels[v] || v}</StatusBadge>;
        },
      },
    },
    ...filterColumns,
  ], [t, lang, data, statusLabels, filterColumns]);

  return { columns, data, fetching, pagination, refresh, decide, decideBulk, statusLabels };
};

// GET / — verilmiş kararlar. Revize Et tek karar POST'udur (status hep 'approved' — brief: "aynı onay
// dialog'u"); Kararlar hook'u kendi başına Onayla/Reddet SUNMAZ (o akış yalnız Bekleyenler'de).
export const useOvertimeDecisionsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getOvertimeApprovals(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => setData([]))
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  const revise = useCallback(async (payload) => {
    try {
      await decideOvertimeApproval({ ...payload, status: 'approved' });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const decisionLabels = useMemo(() => ({
    approved: t('leave.approved'),
    rejected: t('leave.rejected'),
  }), [t]);
  const filterColumns = useTaFilterColumns(t);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('timesheet.employee'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <PersonCell name={v} profilePicture={row?.userProfilePicture} />;
        },
      },
    },
    dateFilterColumn('date', t('timesheet.date'), lang),
    {
      name: 'measuredOvertimeMinutes', label: t('overtimeApproval.measured'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!isLegacyDecision(row)) return formatMinutes(decisionMeasured(row));
          return (
            <StaleWrap>
              <span>{formatMinutes(decisionMeasured(row))}</span>
              <LegacyNote data-legacy="true">{t('overtimeApproval.legacyHint')}</LegacyNote>
            </StaleWrap>
          );
        },
      },
    },
    {
      name: 'approvedMinutes', label: t('overtimeApproval.approvedMinutes'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatMinutes(v) },
    },
    {
      name: 'status', label: t('common.status'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['approved', 'rejected'],
          renderValue: (v) => decisionLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => decisionLabels[v] || v },
        customBodyRender: (v) => <DecisionBadge $status={v}>{decisionLabels[v] || v}</DecisionBadge>,
      },
    },
    {
      name: 'deciderFullName', label: t('overtimeApproval.decider'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => (
          v ? <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.deciderProfilePicture} /> : '-'
        ) },
    },
    {
      name: 'notes', label: t('overtimeApproval.notes'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      // isStale: response alanı doğrudan kullanılır (FE'de yeniden hesaplanmaz). Taze satırlarda hücre boş —
      // rozet yalnız bayat satırda görünür. "Güncel" değer kararın ölçüsüyle aynı türdendir: yeni kayıtta
      // güncel onaylanabilir fazla mesai, eski kayıtta güncel vardiya dışı dakika.
      name: 'isStale', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (v, tableMeta) => {
          if (!v) return null;
          const row = data[tableMeta.rowIndex];
          return (
            <Tooltip text={t('overtimeApproval.staleTooltip')} position="top">
              <StaleWrap>
                <StaleBadge>{t('overtimeApproval.staleBadge')}</StaleBadge>
                <StaleDetail>{t('overtimeApproval.current')}: {formatMinutes(decisionCurrent(row))}</StaleDetail>
              </StaleWrap>
            </Tooltip>
          );
        },
      },
    },
    ...filterColumns,
  ], [t, lang, data, decisionLabels, filterColumns]);

  return { columns, data, fetching, pagination, refresh, revise, decisionLabels };
};
